import {
  NotificationTypes,
  type NotificationType,
  type NotificationResponse,
} from '@/api/types/Notification'
import { formatAsTitle } from '@/utils/format'

const NotificationTypeMeta: Record<NotificationType, { name: string; description: string }> = {
  DIGEST_INDIVIDUALS: {
    name: 'Daily Email Digest for Individuals',
    description: 'Receive a summary of alerts once per day.'
  },
  DIGEST_DELEGATES: {
    name: 'Daily Email Digest for Members',
    description: 'Receive a summary of alerts of assigned individuals once per day.'
  },
  ALERT_COMMENT: {
    name: 'Alert Comment',
    description: 'Get notified when someone comments on an alert that you have been assigned to.'
  },
  ALERT_STATUS_CHANGE: {
    name: 'Alert Status Change',
    description: 'Get notified when the status of an alert that you have been assigned to changes.'
  },
  ALERT_STATUS_TO_TAKEDOWN_FOR_INDIVIDUAL: {
    name: 'Alert Status to Takedown for an Individual',
    description: 'Get notified when an alert status changes to Issue Takedown.'
  },
  ALERT_ASSIGNMENT: {
    name: 'Alert Assignment',
    description: 'Get notified when you are assigned an alert.'
  },
  ALERT_UNASSIGNMENT: {
    name: 'Alert Unassignment',
    description: 'Get notified when an alert you have been assigned to is unassigned.'
  },
  ALERT_ESCALATION: {
    name: 'Alert Escalation',
    description: 'Get notified when an alert is escalated to you.'
  },
  ALERT_UNESCALATION: {
    name: 'Alert Unescalation',
    description: 'Get notified when an alert you have been assigned to is unescalated from you.'
  },
  ALERT_PUBLIC_REPORT: {
    name: 'Alert Public Report',
    description: 'Get notified when a public report is submitted for an individual that you are monitoring.'
  },
}

const NotificationGroupingMeta: Record<string, Partial<NotificationType>[]> = {
  'Daily Digests': [
    'DIGEST_INDIVIDUALS',
    'DIGEST_DELEGATES'
  ],
  'Alerts': [
    'ALERT_COMMENT',
    'ALERT_STATUS_CHANGE',
    'ALERT_STATUS_TO_TAKEDOWN_FOR_INDIVIDUAL',
    'ALERT_ASSIGNMENT',
    'ALERT_UNASSIGNMENT',
    'ALERT_ESCALATION',
    'ALERT_UNESCALATION'
  ],
  'Reports': [
    'ALERT_PUBLIC_REPORT'
  ]
}

const NotificationTypeDetails = NotificationTypes.map(key => {
  const meta = NotificationTypeMeta[key] ?? {}
  return {
    key,
    name: meta.name ?? formatAsTitle(key),
    description:
      meta.description ??
      `Notification for ${formatAsTitle(key.replace(/_/g, ' '))}.`
  }
})

const useNotifications = (notification?: NotificationResponse) => {

  const notificationMessage = (() => {
    if (!notification) return ''

    const creatorName = notification.data.author?.fullName || 'Someone'
    const assignmentToName = notification.data.assignedTo?.fullName || 'Someone'
    const unassignmentFromName = notification.data.unassignedFrom?.fullName || 'Someone'
    const escalateToName = notification.data.escalatedTo?.fullName || 'Someone'
    const deescalateFromName = notification.data.deescalatedFrom?.fullName || 'Someone'
    const newStatusName = formatAsTitle(notification.data.status || 'unknown')

    switch (notification.type) {
      case 'ALERT_COMMENT':
        return <>{creatorName} commented on alert.</>
      case 'ALERT_STATUS_CHANGE':
        return <>{creatorName} changed alert status to <b>{newStatusName}</b>.</>
      case 'ALERT_ASSIGNMENT':
        return <>{creatorName} assigned alert to <b>{assignmentToName}</b>.</>
      case 'ALERT_UNASSIGNMENT':
        return <>{creatorName} unassigned alert from <b>{unassignmentFromName}</b>.</>
      case 'ALERT_ESCALATION':
        return <>{creatorName} escalated alert to <b>{escalateToName}</b>.</>
      case 'ALERT_UNESCALATION':
        return <>{creatorName} unescalated alert from <b>{deescalateFromName}</b>.</>
      case 'ALERT_PUBLIC_REPORT':
        return `${creatorName} submitted a public report for alert.`
      default:
        return 'You have a new notification.'
    }
  })()

  return {
    notificationGrouping: NotificationGroupingMeta,
    notificationTypeDetails: NotificationTypeDetails,
    getNotificationTypeName: (type: NotificationType) => NotificationTypeDetails.find(t => t.key === type)?.name,
    getNotificationTypeDescription: (type: NotificationType) => NotificationTypeDetails.find(t => t.key === type)?.description,
    notificationMessage,
  }
}

export default useNotifications
